import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useStore } from '../../store/useStore';
import { ErrorLine, Section } from './ui';

// --- Dev fields -------------------------------------------------------------

const PALETTE = ['#4d8dff', '#3fb950', '#f0883e', '#a371f7', '#f85149', '#2dd4bf', '#8b949e'];

/** What the fields endpoint returns — a field plus the two things only it knows. */
type ManagedField = {
  id: string;
  slug: string;
  name: string;
  color: string;
  position: number;
  defaultAssigneeId: string | null;
  memberIds: string[];
};

export function FieldsTab() {
  const groupId = useStore((s) => s.activeGroupId);
  const members = useStore((s) => s.members);
  const refresh = useStore((s) => s.refresh);

  // Read from the fields endpoint rather than from the store. The sync delta
  // carries a field's name and colour but not its roster — field membership has
  // no revision of its own — so the store's copy would show every field as empty.
  const [fields, setFields] = useState<ManagedField[]>([]);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!groupId) return;
    const { fields: rows } = await api<{ fields: ManagedField[] }>(`/api/groups/${groupId}/fields`);
    setFields(rows);
  }, [groupId]);

  useEffect(() => {
    void reload().catch(() => undefined);
  }, [reload]);

  /** Every mutation refreshes both views: this roster, and the board's copy. */
  async function apply(what: string, change: () => Promise<unknown>) {
    setError(null);
    try {
      await change();
      await reload();
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : what);
    }
  }

  async function addField(event: FormEvent) {
    event.preventDefault();
    await apply('Could not add the field', async () => {
      await api(`/api/groups/${groupId}/fields`, {
        method: 'POST',
        body: { name, color: PALETTE[fields.length % PALETTE.length] },
      });
      setName('');
    });
  }

  async function saveMembers(fieldId: string, userIds: string[]) {
    await apply('Could not change who works in this field', () =>
      api(`/api/groups/${groupId}/fields/${fieldId}/members`, {
        method: 'PUT',
        body: { userIds },
      }),
    );
  }

  async function saveDefaultAssignee(fieldId: string, userId: string | null) {
    await apply('Could not set the default assignee', () =>
      api(`/api/groups/${groupId}/fields/${fieldId}`, {
        method: 'PATCH',
        body: { defaultAssigneeId: userId },
      }),
    );
  }

  async function removeField(fieldId: string) {
    await apply('Could not remove the field', () =>
      api(`/api/groups/${groupId}/fields/${fieldId}`, { method: 'DELETE' }),
    );
  }

  return (
    <Section
      title="Dev fields"
      description="Members only see tasks in the fields they belong to. Admins see everything. A field can also route its own work, so filing a task by field is the only decision you have to make."
    >
      <div className="flex flex-col gap-2">
        {fields.map((field) => {
          const memberIds = field.memberIds;
          const open = editingId === field.id;
          const routesTo = field.defaultAssigneeId
            ? members.find((m) => m.id === field.defaultAssigneeId)
            : undefined;
          return (
            <div key={field.id} className="card p-4">
              <div className="flex items-center gap-3">
                <span
                  className="size-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: field.color }}
                />
                <span className="text-sm font-medium">{field.name}</span>
                <span className="text-xs text-ink-faint">
                  {memberIds.length} member{memberIds.length === 1 ? '' : 's'}
                </span>
                <span className="truncate text-xs text-ink-faint">
                  {routesTo ? `→ ${routesTo.displayName}` : 'no default assignee'}
                </span>
                <span className="ml-auto flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditingId(open ? null : field.id)}
                    className="btn btn-ghost !py-1 !text-xs"
                  >
                    {open ? 'Done' : 'Configure'}
                  </button>
                  <button
                    type="button"
                    onClick={() => void removeField(field.id)}
                    className="btn btn-danger !py-1 !text-xs"
                  >
                    Remove
                  </button>
                </span>
              </div>

              {open && (
                <div className="animate-rise mt-3 flex flex-col gap-4 border-t border-line pt-3">
                  <div className="flex flex-col gap-1.5">
                    <span className="text-xs font-medium text-ink-soft">Who works here</span>
                    <div className="flex flex-wrap gap-2">
                      {members.map((member) => {
                        const on = memberIds.includes(member.id);
                        return (
                          <button
                            key={member.id}
                            type="button"
                            onClick={() =>
                              void saveMembers(
                                field.id,
                                on
                                  ? memberIds.filter((id) => id !== member.id)
                                  : [...memberIds, member.id],
                              )
                            }
                            className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${
                              on
                                ? 'border-accent bg-accent/15 text-ink'
                                : 'border-line text-ink-soft hover:bg-hover'
                            }`}
                          >
                            {member.displayName}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* The routing rule: a task filed here with nobody named lands on
                      this person, so creating work and assigning it are one step. */}
                  <label className="flex flex-col gap-1.5">
                    <span className="text-xs font-medium text-ink-soft">
                      New tasks here go to
                    </span>
                    <select
                      value={field.defaultAssigneeId ?? ''}
                      onChange={(event) =>
                        void saveDefaultAssignee(field.id, event.target.value || null)
                      }
                      className="field-input max-w-xs text-sm"
                    >
                      <option value="">Nobody — leave them unassigned</option>
                      {members
                        .filter((member) => memberIds.includes(member.id))
                        .map((member) => (
                          <option key={member.id} value={member.id}>
                            {member.displayName}
                          </option>
                        ))}
                    </select>
                    {memberIds.length === 0 && (
                      <span className="text-[11px] text-ink-faint">
                        Add somebody to this field first — work can only be routed to a
                        person who can see it.
                      </span>
                    )}
                  </label>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <form onSubmit={addField} className="flex gap-2">
        <input
          className="field-input text-sm"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="New field name — e.g. Plugins"
          maxLength={60}
          required
        />
        <button type="submit" className="btn btn-ghost">
          Add field
        </button>
      </form>

      <ErrorLine message={error} />
    </Section>
  );
}
